import React from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Star, Eye, Moon, Sparkles, Coins, BookOpen, Heart, Sun, Shuffle, ChevronRight } from 'lucide-react';
import { useAuth } from '../context/AuthContext';

export default function HomePage() {
  const { login, user } = useAuth();
  const navigate = useNavigate();

  const features = [
    { img: 'https://customer-assets.emergentagent.com/job_a07c526d-053f-4cbd-87db-47ed5f2a882e/artifacts/yxp8ksrp_icono-lectura-tarot.png', title: 'Lectura de Tarot', desc: 'Descubre tu destino con los Arcanos Mayores', link: '/tarot' },
    { img: 'https://customer-assets.emergentagent.com/job_a07c526d-053f-4cbd-87db-47ed5f2a882e/artifacts/4pqylxj6_icono-astrologia.png', title: 'Astrologia', desc: 'Horoscopo semanal y carta natal', link: '/astrology' },
    { img: 'https://customer-assets.emergentagent.com/job_a07c526d-053f-4cbd-87db-47ed5f2a882e/artifacts/bzcxgxdp_icono-numerologia.png', title: 'Numerologia', desc: 'El poder de los numeros en tu vida', link: '/numerology' },
    { img: 'https://customer-assets.emergentagent.com/job_a07c526d-053f-4cbd-87db-47ed5f2a882e/artifacts/dqqr3enx_icono-iching.png', title: 'I Ching', desc: 'El Libro de los Cambios, sabiduria milenaria', link: '/iching' },
  ];

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="relative py-24 px-4 overflow-hidden">
        <div className="max-w-4xl mx-auto text-center relative z-10">
          <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8 }}>
            <div className="relative inline-block mb-8">
              <div className="absolute inset-0 rounded-full blur-2xl opacity-20" style={{ background: 'radial-gradient(circle, rgba(230,195,106,0.4), transparent 70%)' }} />
              <img 
                src="https://customer-assets.emergentagent.com/job_mystic-cards-56/artifacts/wfhpmaw5_Madame-Blavatsky--Logo-2_20260216_193202_0000.png"
                alt="Madame Blavatsky" className="w-36 h-36 mx-auto relative"
              />
            </div>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold mb-4 font-heading" data-testid="hero-title">
              <span className="text-[var(--text-primary)]">Madame </span>
              <span className="gold-text">Blavatsky</span>
            </h1>
            <p className="text-base text-gold-300/80 italic mb-2 font-heading tracking-wide">"La verdad se revela a quienes la buscan"</p>
            <p className="text-base text-[var(--text-secondary)] mb-10 max-w-2xl mx-auto">
              Tarot, Astrologia, Numerologia e I Ching en un solo lugar. Conecta con tu destino y encuentra las respuestas que buscas.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <button onClick={() => navigate('/tarot')} className="btn-gold" data-testid="cta-tarot">
                <Shuffle className="w-5 h-5 inline mr-2" /> Lectura Gratuita
              </button>
              {!user && (
                <Link to="/login" className="btn-ghost inline-block" data-testid="cta-register">Crear Cuenta</Link>
              )}
            </div>
          </motion.div>
        </div>
      </section>

      {/* Features */}
      <section className="py-16 px-4">
        <div className="max-w-6xl mx-auto">
          <h2 className="text-2xl sm:text-3xl font-bold text-center mb-12 gold-text font-heading" data-testid="features-title">Explora las Artes Misticas</h2>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {features.map((feature, i) => (
              <motion.div key={i} initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.1 }}
                className="glass-card p-8 cursor-pointer group text-center" onClick={() => navigate(feature.link)} data-testid={`feature-card-${i}`}>
                <img src={feature.img} alt={feature.title} className="w-16 h-16 sm:w-20 sm:h-20 mb-4 object-contain rounded-lg mx-auto" />
                <h3 className="text-lg font-bold mb-2 font-heading">{feature.title}</h3>
                <p className="text-[var(--text-muted)] text-sm">{feature.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Tarot Preview */}
      <section className="py-16 px-4">
        <div className="max-w-6xl mx-auto">
          <div className="glass-card p-8 md:p-12 flex flex-col md:flex-row items-center gap-8">
            <div className="flex-1">
              <h2 className="text-2xl sm:text-3xl font-bold mb-4 font-heading">
                <span className="gold-text">Tirada de 3 Cartas</span>
                <span className="ml-2 text-sm font-normal bg-green-500/20 text-green-400 px-3 py-1 rounded-full font-body">GRATIS</span>
              </h2>
              <p className="text-[var(--text-muted)] mb-6 text-sm">Descubre tu pasado, presente y futuro con los Arcanos Mayores. Sin registro, sin costo.</p>
              <button onClick={() => navigate('/tarot?spread=three_cards')} className="btn-gold" data-testid="start-free-reading">Comenzar Lectura</button>
            </div>
            <div className="flex gap-4">
              {[0, 1, 2].map(i => (
                <motion.div key={i} className="tarot-card" animate={{ y: [0, -8, 0] }} transition={{ duration: 3, delay: i * 0.3, repeat: Infinity }}>
                  <div className="tarot-card-inner"><div className="tarot-card-back" /></div>
                </motion.div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Membership */}
      <section className="py-16 px-4">
        <div className="max-w-4xl mx-auto">
          <h2 className="text-2xl sm:text-3xl font-bold mb-3 gold-text text-center font-heading" data-testid="membership-title">Registrate Gratis</h2>
          <p className="text-[var(--text-muted)] text-center mb-8 text-sm">Crea tu cuenta y recibe estos beneficios al instante</p>
          <div className="glass-card p-8" style={{ borderColor: 'rgba(230,195,106,0.12)' }}>
            <div className="grid sm:grid-cols-2 gap-4">
              {[
                { icon: Coins, text: '20 creditos de bienvenida para gastar' },
                { icon: BookOpen, text: 'Lecturas de Tarot guardadas en tu Diario' },
                { icon: Sun, text: 'Horoscopo diario + Carta del Dia' },
                { icon: Star, text: 'Perfil Cosmico (Sol, Luna, Ascendente, Camino de Vida)' },
                { icon: Heart, text: 'Lectura de cumpleanos gratuita' },
                { icon: Moon, text: 'Acceso a tiradas gratuitas ilimitadas' },
              ].map((benefit, i) => (
                <div key={i} className="flex items-center gap-3 p-3 rounded-lg bg-white/[0.03]">
                  <benefit.icon className="w-5 h-5 text-gold-300 shrink-0" />
                  <span className="text-sm text-[var(--text-secondary)]">{benefit.text}</span>
                </div>
              ))}
            </div>
            {!user && (
              <div className="text-center mt-6">
                <Link to="/login" className="btn-gold inline-block" data-testid="membership-cta">Crear Mi Cuenta Gratis</Link>
              </div>
            )}
          </div>
        </div>
      </section>

      {/* Pricing */}
      <section className="py-16 px-4">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-2xl sm:text-3xl font-bold mb-4 gold-text font-heading" data-testid="pricing-title">Desbloquea Lecturas Premium</h2>
          <p className="text-[var(--text-muted)] mb-8 text-sm">Adquiere creditos para acceder a tiradas mas profundas y completas</p>
          <div className="grid sm:grid-cols-3 gap-6">
            {[
              { name: 'Iniciado', credits: 15, price: '$1.99' },
              { name: 'Mistico', credits: 50, price: '$4.99', popular: true },
              { name: 'Celestial', credits: 120, price: '$9.99' },
            ].map((pkg, i) => (
              <div key={i} className={`glass-card p-6 ${pkg.popular ? 'glow-gold' : ''}`} style={pkg.popular ? { borderColor: 'rgba(230,195,106,0.25)' } : {}}>
                {pkg.popular && <span className="text-xs uppercase tracking-wider text-gold-300 mb-2 block font-heading">Popular</span>}
                <h3 className="text-xl font-bold mb-2 font-heading">{pkg.name}</h3>
                <p className="text-3xl font-bold text-purple-400 mb-2">{pkg.credits}</p>
                <p className="text-[var(--text-muted)] text-sm mb-4">creditos</p>
                <p className="text-2xl font-bold mb-4">{pkg.price}</p>
                <button onClick={() => navigate('/credits')} className={pkg.popular ? 'btn-gold w-full' : 'btn-ghost w-full'} data-testid={`buy-package-${i}`}>Comprar</button>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-8 px-4 border-t border-white/5">
        <div className="max-w-6xl mx-auto flex flex-col md:flex-row items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <img src="https://customer-assets.emergentagent.com/job_mystic-cards-56/artifacts/wfhpmaw5_Madame-Blavatsky--Logo-2_20260216_193202_0000.png" alt="Madame Blavatsky" className="w-8 h-8 rounded-full" />
            <span className="font-decorative text-gold-300 text-sm">Madame Blavatsky</span>
          </div>
          <div className="flex items-center gap-4 text-[var(--text-muted)] text-sm">
            <Link to="/privacy" className="hover:text-gold-300 transition-colors">Politica de Privacidad</Link>
            <span>·</span>
            <Link to="/terms" className="hover:text-gold-300 transition-colors">Condiciones del Servicio</Link>
          </div>
          <p className="text-[var(--text-muted)] text-xs">© 2026 Madame Blavatsky</p>
        </div>
      </footer>
    </div>
  );
}
